import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Percent, Euro, CreditCard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function PosChargesSettings() {
  const { authAxios } = useAuth();
  const [rule, setRule] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authAxios.get("/finance-admin/settings/pos-charges")
      .then(r => setRule({ type: r.data.type, value: String(r.data.value ?? 0) }))
      .catch(() => toast.error("Failed to load POS charges"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const v = parseFloat(rule.value);
    if (isNaN(v) || v < 0 || (rule.type === "percent" && v > 100)) return toast.error("Enter 0–100% or a non-negative amount");
    setSaving(true);
    try { await authAxios.put("/finance-admin/settings/pos-charges", { type: rule.type, value: v }); toast.success("POS charges saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!rule) return null;
  const v = parseFloat(rule.value) || 0;
  const preview = rule.type === "percent" ? (25 * v) / 100 : v;
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm max-w-xl mt-4" data-testid="pos-charges-settings">
      <h2 className="font-bold text-lg text-[#1e3a5f] mb-1 flex items-center gap-2"><CreditCard className="w-5 h-5" /> POS Payment Processing Charges</h2>
      <p className="text-sm text-slate-500 mb-4">Added on top for every card/digital sale at the accountant desk — coupon sales, walk-in tickets and wallet top-ups — regardless of member type. Kept separately from revenue.</p>
      <div className="grid grid-cols-[auto_1fr] gap-2 items-center mb-3">
        <div className="flex gap-1">
          {[["percent", Percent], ["fixed", Euro]].map(([id, Icon]) => (
            <button key={id} type="button" onClick={() => setRule({ ...rule, type: id })} data-testid={`pos-charges-mode-${id}`}
              className={`p-2.5 rounded-lg border transition-colors ${rule.type === id ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
        <input type="number" step="0.01" min="0" value={rule.value} onChange={e => setRule({ ...rule, value: e.target.value })}
          className="w-full px-3 py-2.5 rounded-lg border font-mono" data-testid="pos-charges-value-input" />
      </div>
      <p className="text-xs text-slate-500 mb-4" data-testid="pos-charges-preview">On a €25.00 card sale the buyer pays €{(25 + preview).toFixed(2)} (€{preview.toFixed(2)} POS charge{rule.type === "fixed" ? ", flat per transaction" : ""}).</p>
      <button onClick={save} disabled={saving} className="px-6 py-3 rounded-full bg-[#1e3a5f] text-white font-bold flex items-center gap-2 disabled:opacity-50" data-testid="pos-charges-save-btn">
        <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save POS Charges"}
      </button>
    </div>
  );
}
